import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Form, Tabs } from 'antd';
import { cloneDeep, toMerged } from 'es-toolkit';
import moment from 'moment';

import { setAppLoader } from '@/redux/common/CommonActions';
import { getBranchDetails, getTrailerDetailsEffect } from '@/redux/Savers/SaversEffects';
import PrimaryLabel from '@/views/components/Labels/PrimaryLabel';
import AlertModal from '@/views/components/Modals/AlertModal';
import Drawer from '@/views/components/Modals/Drawer';
import TrailerTransferModal from '@/views/screens/Savers/TrailerTransferModal';

import { INITIAL_TRAILER_DETAILS, TAB_KEYS } from './constants';
import useFieldsData from './hooks/useFieldsData';
import TrailerManagement from './TrailerManagement';
import {
  BuildDestInfo,
  BuildOrgInfo,
  BuildProdDetailsInfo,
  CreateMovementData,
  createShipmentData,
  DestinationDataBuilder,
  getShipmentDetails,
  OriginDataBuilder,
} from './utils';

const TrailerMainPage = () => {
  const [formPickupManagement] = Form.useForm();
  const [formDeliveryManagement] = Form.useForm();
  const dispatch = useDispatch();
  const { state } = useLocation();

  const userType = state?.userType;
  const shipmentData = state?.data?.[0] ?? {};
  const { isMultiple, pickupStatus, deliveryStatus, pickupLabel, deliveryLabel, nonMultiLabel, nonMultiStatus } =
    getShipmentDetails(shipmentData);
  const companyId = state.companyId;
  const requestID = state.requestID;
  const requestUserType = state.requestUserType;

  const suppliers = useSelector((state) => state.savers.supplierList.data);

  const [activeTabName, setActiveTabName] = useState(TAB_KEYS.PICKUP);
  const [pickupFormValues, setPickupFormValues] = useState(INITIAL_TRAILER_DETAILS);
  const [deliveryFormValues, setDeliveryFormValues] = useState(INITIAL_TRAILER_DETAILS);
  const [trailerId, setTrailerId] = useState(0);
  const [loadId, setLoadId] = useState(0);
  const [transferHidden, setTransferHidden] = useState(false);
  const [saveShipmentData, setSaveShipmentData] = useState([]);
  const [saveMovementData, setSaveMovementData] = useState([]);
  const [companyIdVal, setCompanyIdVal] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [userStatus, setUserStatus] = useState('');
  const [pickupVendorId, setPickupVendorId] = useState('');
  const [deliveryVendorId, setDeliveryVendorId] = useState('');
  const [productType, setProductType] = useState('0');
  const isPickupLoaded = pickupLabel === 'Pickup Loaded' || nonMultiLabel === 'Pickup Loaded';
  const isDirectPartner = isPickupLoaded && requestUserType === 'Supplier';
  const [checkOptions, setCheckOptions] = useState({
    directFromPartner: isDirectPartner,
    rollDoor: false,
  });
  const { createdBy, shipmentNumber, branchKey, poNumberValue, soNumberValue, unitSize } = useFieldsData(
    companyId,
    requestID,
    formPickupManagement,
    formDeliveryManagement,
    setPickupFormValues,
    setDeliveryFormValues,
    isMultiple,
    nonMultiLabel,
    setCheckOptions,
    setPickupVendorId,
    setDeliveryVendorId,
    activeTabName,
    setTrailerId,
    setProductType,
  );
  const handleValuesChange = (changedValues) => {
    let transformedValues;
    if (activeTabName === TAB_KEYS.PICKUP || !isMultiple) {
      transformedValues = cloneDeep(pickupFormValues);
    } else {
      transformedValues = cloneDeep(deliveryFormValues);
    }

    if (changedValues.orgLocations) {
      transformedValues.orgLocations = (changedValues.orgLocations || []).map((prev, index) => {
        const changed = changedValues.orgLocations?.[index];
        if (!changed) {
          return prev;
        }

        if (changed?.orgShipLocation && typeof changed.orgShipLocation === 'object') {
          return {
            ...prev,
            ...changed,
            orgLocationId: changed.orgShipLocation.value,
            orgShipLocation: changed.orgShipLocation.label,
          };
        }
        // biome-ignore lint/correctness/noUnusedVariables: removing this fields from current values
        const { orgLocationId, orgShipLocation, ...otherUpdatedFields } = changed;
        return { ...prev, ...otherUpdatedFields };
      });
    }

    if (changedValues.Commodity && typeof changedValues.Commodity === 'object') {
      transformedValues.productType = changedValues.Commodity.value;
      transformedValues.Commodity = changedValues.Commodity.label;
      setProductType(changedValues.Commodity.value);
    }

    if (changedValues.destLocations) {
      transformedValues.destLocations = (changedValues.destLocations || []).map((prev, index) => {
        const changed = changedValues.destLocations?.[index];
        if (!changed) {
          return prev;
        }

        if (changed?.destConsignee && typeof changed.destConsignee === 'object') {
          return {
            ...prev,
            ...changed,
            destConsignee: changed.destConsignee.label,
            destLocationId: changed.destConsignee.value,
          };
        }

        // biome-ignore lint/correctness/noUnusedVariables: removing this fields from current values
        const { destLocationId, destConsignee, ...otherUpdatedFields } = changed;
        return { ...prev, ...otherUpdatedFields };
      });
    }
    if (activeTabName === TAB_KEYS.PICKUP) {
      setPickupFormValues((prevValues) => toMerged(prevValues, transformedValues));
    } else if (activeTabName === TAB_KEYS.DELIVERY) {
      setDeliveryFormValues((prevValues) => toMerged(prevValues, transformedValues));
    }
  };

  const getOrgSpotDate = (trailer) => {
    const useStorage =
      (!isMultiple && nonMultiLabel === 'Delivery Empty') || (isMultiple && deliveryLabel === 'Delivery Empty');

    const date = useStorage ? trailer.storageDate : trailer.loadedDate;
    return date ? moment(date) : null;
  };

  const handleTrailerIdChange = async () => {
    try {
      dispatch(setAppLoader(true));
      const { data } = await getTrailerDetailsEffect(trailerId);
      if (!data || data.length === 0) {
        return;
      }
      const trailer = data[0];
      setPickupVendorId(trailer.vendorId);
      if (isMultiple) {
        setDeliveryVendorId(trailer.vendorId);
      }
      const getOrgLocations = (shouldNullifyLocation, existingOrgLocations = []) =>
        data.map((trailer, index) => ({
          orgBookOks: trailer.bookOks,
          orgBookWeight: trailer.book,
          orgFurn: trailer.furn,
          orgLocationId: trailer.locationId,
          orgMiscOks: trailer.miscOKs,
          orgMiscWeight: trailer.misc,
          orgOLI: trailer.oli,
          orgShipLocation: shouldNullifyLocation
            ? (existingOrgLocations[index]?.orgShipLocation ?? null)
            : trailer.branchDesc,
          orgSoftOks: trailer.softOks,
          orgSoftWeight: trailer.soft,
          orgSpotDate: getOrgSpotDate(trailer),
        }));

      const getDestLocations = (existingDestLocations) =>
        data.map((trailer, index) => ({
          destBookOks: trailer.bookOks,
          destBookWeight: trailer.book,
          destConsignee: existingDestLocations[index]?.destConsignee,
          destETA: null,
          destFurn: trailer.furn,
          destLocationId: existingDestLocations[index]?.destLocationId,
          destMiscOks: trailer.miscOKs,
          destMiscWeight: trailer.misc,
          destOLI: trailer.oli,
          destSoftOks: trailer.softOks,
          destSoftWeight: trailer.soft,
        }));
      const commonValues = {
        DeliveryRentalCompany: trailer.unitRentalCompany,
        entryDate: trailer.entryDate && moment(trailer.entryDate),
        PickupRentalCompany:
          (trailer.unitRentalCompany && trailer.unitRentalCompany) !== 'NULL' ? trailer.unitRentalCompany : null,
        UnitSize: trailer?.unitSizeID,
      };
      setCheckOptions((prev) => ({
        ...prev,
        rollDoor: trailer.rollDoor,
      }));

      if (userStatus.toLowerCase().includes('pickup')) {
        const existingPickupValues = formPickupManagement.getFieldsValue();
        const finalPickupValues = {
          ...existingPickupValues,
          ...commonValues,
          destLocations: getDestLocations(existingPickupValues.destLocations),
          orgLocations: getOrgLocations(true, existingPickupValues.orgLocations),
        };

        formPickupManagement.setFieldsValue(finalPickupValues);
        setPickupFormValues(finalPickupValues);
      } else if (isMultiple && userStatus.toLowerCase().includes('delivery')) {
        const existingDeliveryValues = formDeliveryManagement.getFieldsValue();
        const finalDeliveryValues = {
          ...existingDeliveryValues,
          ...commonValues,
          destLocations: getDestLocations(existingDeliveryValues.destLocations),
          orgLocations: getOrgLocations(false, existingDeliveryValues.orgLocations),
        };
        formDeliveryManagement.setFieldsValue(finalDeliveryValues);
        setDeliveryFormValues((prev) => ({
          ...prev,
          ...finalDeliveryValues,
        }));
      } else {
        const existingDeliveryValues = formPickupManagement.getFieldsValue();
        const finalDeliveryValues = {
          ...existingDeliveryValues,
          ...commonValues,
          destLocations: getDestLocations(existingDeliveryValues.destLocations),
          orgLocations: getOrgLocations(false, existingDeliveryValues.orgLocations),
        };
        formPickupManagement.setFieldsValue(finalDeliveryValues);
        setPickupFormValues((prev) => ({
          ...prev,
          ...finalDeliveryValues,
        }));
      }
    } catch (error) {
      console.log('error', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const handleUserStatus = (data) => {
    setUserStatus(data);
  };

  useEffect(() => {
    const selectedPartner =
      activeTabName === TAB_KEYS.PICKUP ? pickupFormValues.selPartner : deliveryFormValues.selPartner;

    if (selectedPartner && suppliers?.length) {
      const foundRow = suppliers.find((row) => row.branchCustRef === selectedPartner);
      setCompanyIdVal(foundRow?.billToName || '');
    }
  }, [suppliers, activeTabName, pickupFormValues.selPartner, deliveryFormValues.selPartner]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependencies not required
  useEffect(() => {
    handleTrailerIdChange();
  }, [trailerId]);

  useEffect(() => {
    if (requestUserType === 'Supplier') {
      setCheckOptions((prev) => ({
        ...prev,
        directFromPartner: true,
      }));
    }
  }, [requestUserType]);

  const changeActiveTab = (key) => {
    setActiveTabName(key);
  };

  const validateField = (field, message) => {
    if (!field) {
      setAlertMessage(message);
      setAlertOpen(true);
      return false;
    }
    return true;
  };

  const validateDates = () => {
    const fieldsPickup = formPickupManagement.getFieldsValue();
    const pickupSpotDate = fieldsPickup.orgLocations?.[0]?.orgSpotDate;
    const pickupETA = fieldsPickup.destLocations?.[0]?.destETA;
    const pickupETD = fieldsPickup.orgLocations?.[0]?.orgETD;

    if (
      !validateField(pickupSpotDate, 'Please select a spot date') ||
      !validateField(pickupETA, 'Please select an ETA date') ||
      !validateField(pickupETD, 'Please select an ETD date')
    ) {
      return false;
    }
    if (moment(pickupETD).isBefore(moment(pickupSpotDate), 'day')) {
      setAlertMessage('Pickup ETD must be the same as or after spot date.');
      setAlertOpen(true);
      return false;
    }

    if (moment(pickupETA).isBefore(moment(pickupSpotDate), 'day')) {
      setAlertMessage('Pickup ETA must be the same as or after spot date.');
      setAlertOpen(true);
      return false;
    }

    if (moment(pickupETA).isBefore(moment(pickupETD), 'day')) {
      setAlertMessage('ETA must be the same as or later than ETD.');
      setAlertOpen(true);
      return false;
    }

    if (isMultiple && userStatus.toLowerCase().includes('delivery')) {
      const fieldsDelivery = formDeliveryManagement.getFieldsValue();
      const deliverySpotDate = fieldsDelivery.orgLocations?.[0]?.orgSpotDate;
      const deliveryETA = fieldsDelivery.destLocations?.[0]?.destETA;
      const deliveryETD = fieldsDelivery.orgLocations?.[0]?.orgETD;

      if (
        !validateField(deliverySpotDate, 'Please select a spot date for delivery') ||
        !validateField(deliveryETA, 'Please select an ETA date for delivery') ||
        !validateField(deliveryETD, 'Please select an ETD date for delivery')
      ) {
        return false;
      }

      if (!moment(deliveryETD).isAfter(moment(deliverySpotDate))) {
        setAlertMessage('Delivery ETD should be the same as or after spot date');
        setAlertOpen(true);
        return false;
      }

      if (!moment(deliveryETA).isAfter(moment(deliverySpotDate))) {
        setAlertMessage('Delivery ETA should be the same as or after spot date');
        setAlertOpen(true);
        return false;
      }
      if (moment(deliveryETA).isBefore(moment(deliveryETD))) {
        setAlertMessage('Delivery ETA should be the same as or after ETD.');
        setAlertOpen(true);
        return false;
      }
    }

    return true;
  };
  const validateCommodity = () => {
    if (!isMultiple) {
      if (userStatus.toLowerCase().includes('loaded')) {
        const fields = formPickupManagement.getFieldsValue();

        const commodity = fields?.Commodity;
        if (!validateField(commodity, 'Please select a commodity')) {
          return false;
        }
      }
    }
    if (isMultiple) {
      const fieldsDelivery = formDeliveryManagement.getFieldsValue();
      const deliveryCommodity = fieldsDelivery?.Commodity;

      const fieldsPickup = formPickupManagement.getFieldsValue();
      const pickupCommodity = fieldsPickup?.Commodity;
      if (pickupLabel.toLocaleLowerCase().includes('loaded')) {
        if (!validateField(pickupCommodity, 'Please select a commodity for pickup')) {
          return false;
        }
      }

      if (deliveryLabel.toLowerCase().includes('loaded')) {
        if (!validateField(deliveryCommodity, 'Please select a commodity for delivery')) {
          return false;
        }
      }
    }

    return true;
  };

  const validateShippingLocation = () => {
    const pickupFields = formPickupManagement.getFieldsValue();
    const pickShippingLocation = pickupFields?.orgLocations[0]?.orgShipLocation;
    if (!validateField(pickShippingLocation, 'Please select Shipping Location')) {
      return false;
    }

    if (isMultiple) {
      const deliveryFields = formDeliveryManagement.getFieldsValue();
      const deliveryShippingLocation = deliveryFields?.orgLocations?.[0]?.orgShipLocation;

      if (!validateField(deliveryShippingLocation, 'Please select Delivery Shipping Location')) {
        return false;
      }
    }

    return true;
  };

  const onFormSubmit = async () => {
    try {
      if (activeTabName === TAB_KEYS.PICKUP && isMultiple) {
        changeActiveTab(TAB_KEYS.DELIVERY);
        return;
      }
      if (!validateShippingLocation()) {
        return;
      }
      if (!validateCommodity()) {
        return;
      }

      if (!validateDates()) {
        return;
      }

      dispatch(setAppLoader(true));

      const fetchDetails = async (locations, type) => {
        const detailsArray = await Promise.all(
          locations.map(async (trailerDetail) => {
            const locationId = Number.parseInt(
              type === 'org' ? trailerDetail.orgLocationId : trailerDetail.destLocationId,
              10,
            );
            if (!locationId) {
              setAlertMessage('Please select a Trailer#');
              setAlertOpen(true);
              return;
            }
            const { data } = await getBranchDetails(locationId);

            if (data) {
              const prefix = type === 'org' ? 'org' : 'dest';
              Object.assign(data, {
                [`${prefix}name`]: data.branchDesc,
                [`${prefix}address1`]: data.branchAddr1,
                [`${prefix}city`]: data.branchCity,
                [`${prefix}state`]: data.branchState,
                [`${prefix}zip`]: data.branchZip,
                [`${prefix}country`]: data.countryCode,
                [`${prefix}custref`]: data.branchCustRef,
              });
            }
            return data;
          }),
        );

        return type === 'org'
          ? OriginDataBuilder(detailsArray, createdBy)
          : DestinationDataBuilder(detailsArray, createdBy);
      };

      const commonParams = {
        branchKey,
        companyId,
        createdBy,
        poNumberValue,
        requestID,
        shipmentNumber,
        soNumberValue,
        trailerId,
      };

      const processShipment = async (formValues) => {
        return {
          formValues,
          shipmentDestinationInformation: await fetchDetails(formValues.destLocations, 'dest'),
          shipmentOriginInformation: await fetchDetails(formValues.orgLocations, 'org'),
          shipmentProductInformation: BuildProdDetailsInfo(formValues, createdBy),
        };
      };

      const movementShipment = (formValues) => ({
        trailerRequest: BuildDestInfo(formValues.destLocations, trailerId),
        trailerShipping: BuildOrgInfo(formValues.orgLocations, trailerId),
      });
      const shipmentPayload = [createShipmentData({ ...commonParams, ...(await processShipment(pickupFormValues)) })];
      const newShipmentData = cloneDeep(shipmentData);
      if (isMultiple) {
        newShipmentData.isEmptyDelivered = false;
        newShipmentData.isLoadedDelivered = false;
      }
      const movementPayload = [
        CreateMovementData({
          branchKey,
          checkOptions,
          formValues: pickupFormValues,
          productType,
          requestID,
          shipmentData: newShipmentData,
          ...movementShipment(pickupFormValues),
        }),
      ];
      if (isMultiple) {
        shipmentPayload.push(createShipmentData({ ...commonParams, ...(await processShipment(deliveryFormValues)) }));
        const deliveryShipmentData = cloneDeep(shipmentData);
        deliveryShipmentData.isEmptyPickUp = false;
        deliveryShipmentData.isLoadedPickUp = false;
        movementPayload.push(
          CreateMovementData({
            branchKey,
            checkOptions,
            formValues: deliveryFormValues,
            requestID,
            shipmentData: deliveryShipmentData,
            ...movementShipment(deliveryFormValues),
          }),
        );
      }
      setSaveMovementData(movementPayload);
      setSaveShipmentData(shipmentPayload);
      setTransferHidden(true);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  const closeTransferModal = () => {
    setTransferHidden(!transferHidden);
  };

  const handleOptionToggle = (e) => {
    const { name } = e.target;
    setCheckOptions((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const commonProps = {
    activeTabName,
    checkOptions,
    companyId,
    editMode: true,
    handleOptionToggle,
    handleUserStatus,
    handleValuesChange,
    isMultiple,
    onFormSubmit,
    setDeliveryFormValues,
    setLoadId,
    setPickupFormValues,
    setTrailerId,
    userType,
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependencies not required
  const tabItems = useMemo(
    () => [
      {
        children: (
          <TrailerManagement
            {...commonProps}
            formTrailerRequestManagement={formPickupManagement}
            status={pickupStatus}
            label={pickupLabel}
            initialValues={pickupFormValues}
          />
        ),
        key: TAB_KEYS.PICKUP,
        label: TAB_KEYS.PICKUP,
      },
      {
        children: (
          <TrailerManagement
            {...commonProps}
            formTrailerRequestManagement={formDeliveryManagement}
            status={deliveryStatus}
            label={deliveryLabel}
            initialValues={deliveryFormValues}
          />
        ),
        key: TAB_KEYS.DELIVERY,
        label: TAB_KEYS.DELIVERY,
      },
    ],
    [pickupLabel, deliveryLabel, activeTabName, pickupFormValues, deliveryFormValues],
  );

  const onAlertConfirm = () => {
    setAlertOpen(!alertOpen);
  };

  return (
    <div>
      {isMultiple ? (
        <Tabs
          className="page-level-tab-container savers-tab"
          onChange={changeActiveTab}
          activeKey={activeTabName}
          items={tabItems}
        />
      ) : (
        <TrailerManagement
          {...commonProps}
          formTrailerRequestManagement={formPickupManagement}
          status={nonMultiStatus}
          label={nonMultiLabel}
          initialValues={pickupFormValues}
          requestUserType={requestUserType}
        />
      )}

      <Drawer
        width="50%"
        open={transferHidden}
        headerContents={<PrimaryLabel className="text-white">Carrier Selection</PrimaryLabel>}
        onClose={closeTransferModal}
      >
        <TrailerTransferModal
          trailerName={trailerId}
          movementdata={saveMovementData}
          shipmentdata={saveShipmentData}
          shipmentNo={shipmentNumber}
          trailerSizeValue={unitSize}
          companyIdVal={companyIdVal}
          handler={closeTransferModal}
          isMultipleUserDetails={isMultiple}
          pickupVendorId={pickupVendorId}
          deliveryVendorId={deliveryVendorId}
          nonMultiLabel={nonMultiLabel}
          pickupLabel={pickupLabel}
          deliveryLabel={deliveryLabel}
          loadId={loadId}
          requestUserType={requestUserType}
        />
      </Drawer>
      <AlertModal
        title="Alert"
        open={alertOpen}
        description={alertMessage}
        onOk={onAlertConfirm}
        onClose={onAlertConfirm}
      />
    </div>
  );
};

export default TrailerMainPage;
